import { FC, memo, useEffect, useRef, useState } from 'react';

import { Text, Types } from '@marriott/mi-ui-library';
import { ComparisonSlotDropdownProps } from './ComparisonSlotDropdown.types';
import { StyledComparisonSlotDropdown } from './ComparisonSlotDropdown.styles';

export const ComparisonSlotDropdown: FC<ComparisonSlotDropdownProps> = memo(
  ({
    options,
    ariaLabel = 'Select a card to compare',
    disabled = false,
    selectedValue,
    onChange,
  }: ComparisonSlotDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => {
      if (!disabled) setIsOpen(prev => !prev);
    };

    const handleSelect = (value: string) => {
      onChange(value);
      setIsOpen(false);
    };

    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const selectedOption = options.find(opt => opt.value === selectedValue);

    return (
      <StyledComparisonSlotDropdown ref={containerRef}>
        <div className={`m-input-select-field-v3 ${isOpen ? 'is-active' : ''}`}>
          <label htmlFor="dropdown-comparison-slot">{'Credit Cards'}</label>

          {/* Hidden native select for accessibility and tests (keeps options as <option> elements) */}
          <select
            id="dropdown-comparison-slot"
            aria-label={ariaLabel}
            value={selectedValue}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleSelect(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
            data-testid="comparison-select-hidden"
          >
            <option value="">Select a Card</option>
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div
            className="select-wrapper"
            onClick={handleToggle}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                e.preventDefault();
                handleToggle();
              }
            }}
            role="combobox"
            tabIndex={0}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={`dropdown-options-${ariaLabel}`}
          >
            <div className="selected-option">
              {selectedOption ? <span>{selectedOption.label}</span> : <div className="placeholder">Select a Card</div>}
            </div>
            <span className="select-icon icon-dropdown-down"></span>
          </div>

          {isOpen && (
            <ul id={`dropdown-options-${ariaLabel}`} className="dropdown-options" role="listbox">
              {options.map(option => (
                <li
                  key={option.value}
                  className="dropdown-option d-flex p-3"
                  onClick={() => handleSelect(option.value)}
                  role="option"
                  aria-selected={selectedValue === option.value}
                >
                  <img src={option.imagePath} alt={option.label} className="option-image mr-2" />
                  <Text element={Types.tags.span} fontSize={Types.size.small} copyText={option.label} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </StyledComparisonSlotDropdown>
    );
  }
);

ComparisonSlotDropdown.displayName = 'ComparisonSlotDropdown';
